import { useQuery } from '@tanstack/react-query';
import { FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import { useUniversityOptions } from '../../../hooks/useUniversityOptions';
import { LookupRecord } from '../../../api/masterData';
import { useApplicationFormApi } from './applicationFormApi';
import { SearchableLookupField } from '../../leads/wizard/SearchableLookupField';
import { isoToFlagEmoji } from '../../leads/wizard/wizardUtils';
import { WizardValues } from './wizardTypes';

interface Step5Props {
  values: WizardValues;
  setField: <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => void;
}

export const Step5PreferredStudy = ({ values, setField }: Step5Props) => {
  const { listLookup } = useApplicationFormApi();
  const qualificationLevelsQuery = useQuery({
    queryKey: ['lookup', '/master-data/academic-levels'],
    queryFn: () => listLookup('/master-data/academic-levels', { activeOnly: true }),
  });
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
  });
  // Active universities, plus this record's own saved one if deactivated.
  const universitiesQuery = useUniversityOptions([values.alternativeUniversityId]);

  const flagFor = (option: LookupRecord) =>
    typeof option.iso2 === 'string' ? isoToFlagEmoji(option.iso2) : undefined;

  return (
    <Stack spacing={2}>
      <SearchableLookupField
        label="Preferred country"
        options={countriesQuery.data}
        value={values.countryId}
        onChange={(id) => setField('countryId', id)}
        getFlag={flagFor}
      />
      <TextField
        select
        label="Preferred qualification"
        fullWidth
        value={values.preferredQualificationLevelId}
        onChange={(e) => setField('preferredQualificationLevelId', e.target.value ? Number(e.target.value) : '')}
      >
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        {qualificationLevelsQuery.data?.map((l) => (
          <MenuItem key={l.id as number} value={l.id as number}>
            {String(l.name)}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Preferred city"
        fullWidth
        value={values.preferredCity}
        onChange={(e) => setField('preferredCity', e.target.value)}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SearchableLookupField
          label="Alternative country"
          options={countriesQuery.data}
          value={values.alternativeCountryId}
          onChange={(id) => setField('alternativeCountryId', id)}
          getFlag={flagFor}
        />
        <SearchableLookupField
          label="Alternative university"
          options={universitiesQuery.data}
          value={values.alternativeUniversityId}
          onChange={(id) => setField('alternativeUniversityId', id)}
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={values.scholarshipRequired}
            onChange={(e) => setField('scholarshipRequired', e.target.checked)}
          />
        }
        label="Scholarship required"
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="English requirement"
          fullWidth
          value={values.englishTestType}
          onChange={(e) => setField('englishTestType', e.target.value as WizardValues['englishTestType'])}
        >
          <MenuItem value="NONE">No English test</MenuItem>
          <MenuItem value="IELTS">IELTS</MenuItem>
          <MenuItem value="PTE">PTE</MenuItem>
          <MenuItem value="TOEFL">TOEFL</MenuItem>
          <MenuItem value="DUOLINGO">Duolingo</MenuItem>
        </TextField>
        {values.englishTestType && values.englishTestType !== 'NONE' && (
          <>
            <TextField
              label="Score"
              fullWidth
              value={values.englishTestScore}
              onChange={(e) => setField('englishTestScore', e.target.value)}
            />
            <TextField
              label="Exam date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.englishTestDate}
              onChange={(e) => setField('englishTestDate', e.target.value)}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};
